import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import {
  Microsoft365License,
  Microsoft365LicenseCount,
  Microsoft365LicenseSettings,
  Microsoft365LicensesView,
  PrivilegedTenant,
} from '../../../shared/models/generated/ziroModels';
import { forkJoin, Subscription } from 'rxjs';
import { MsLicenseManagementEntity } from './license-management-form.component';
import { Microsoft365LicenseSettingsResource } from '../../../shared/resources/microsoft-365-license-settings.resource';
import { Microsoft365LicensesViewResource } from '../../../shared/resources/microsoft-365-licenses-view.resource';
import { ActivatedRoute } from '@angular/router';
import { TenantNavigationService } from '../../services/tenant-navigation.service';

@Component({
  selector: 'ziro-microsoft-license-management',
  templateUrl: './license-management.component.html',
  styleUrls: ['../../admin-page.scss'],
  providers: [Microsoft365LicenseSettingsResource, TenantNavigationService],
  standalone: false,
})
export class MicrosoftLicenseManagementComponent implements OnInit, OnDestroy {
  private _microsoft365LicensesViewResource = inject(Microsoft365LicensesViewResource);
  private _microsoft365LicenseSettingsResource = inject(Microsoft365LicenseSettingsResource);
  private _route = inject(ActivatedRoute);
  private _tenantNavigationService = inject(TenantNavigationService);

  isLoading = true;
  formEntity: MsLicenseManagementEntity;
  usageLocationOptions: string[];
  licensesOptions: string[];
  ms365Licenses: Microsoft365LicenseCount[];
  tenantInfo: PrivilegedTenant;

  private _subs = new Subscription();

  ngOnInit(): void {
    const tenantSub = this._tenantNavigationService.getTenantInfo(this._route).subscribe((tenantInfo) => {
      this.tenantInfo = tenantInfo;
      this._initBreadcrumbs();
      this._initFormData();
    });

    this._subs.add(tenantSub);
  }

  ngOnDestroy(): void {
    this._subs.unsubscribe();
  }

  private _initBreadcrumbs(): void {
    this._tenantNavigationService.buildTenantBreadcrumbs(this.tenantInfo, [
      { label: 'tkey;admin.microsoft.license_management.title' },
    ]);
  }

  private _initFormData(): void {
    forkJoin([
      this._microsoft365LicensesViewResource.getByTenantId(this.tenantInfo.tenantId),
      this._microsoft365LicenseSettingsResource.get(this.tenantInfo.tenantId),
    ]).subscribe((data: [Microsoft365LicensesView, Microsoft365LicenseSettings]) => {
      this.usageLocationOptions = data[0].usageLocationsList;
      this.ms365Licenses = data[0].microsoft365LicenseCountJsonList;
      this.licensesOptions = data[0].microsoft365LicenseCountJsonList.map(
        (count: Microsoft365LicenseCount) =>
          count.microsoft365LicenseJson.productName || count.microsoft365LicenseJson.skuPartNumber,
      );
      const licenseSettings = data[1];

      this.formEntity = {
        usageLocation: {
          show: licenseSettings.usageLocation.show,
          required: licenseSettings.usageLocation.required,
          defaultValue: licenseSettings.usageLocation.defaultValue,
          possibleOptions: licenseSettings.usageLocation.possibleOptions,
        },
        licenses: {
          show: true,
          required: false,
          defaultValues: licenseSettings.writeLicenseList.map(
            (license: Microsoft365License) => license.productName || license.skuPartNumber,
          ),
          possibleOptions: licenseSettings.readOnlyLicenseList.map(
            (license: Microsoft365License) => license.productName || license.skuPartNumber,
          ),
        },
      };

      this.isLoading = false;
    });
  }
}
